import { CLOUD_API_URL } from '../config/serverConfig';
import {
  NO_SESSION_FAILURE,
  REQUEST_TIMEOUT_MS,
  postOnboardingAttempt,
  type SaveFailure,
} from './onboardingSaveAttempt';

export type OnboardingCompletePayload = {
  genres: string[];
  artists: string[];
  streamingService: string | null;
};

export type SaveOnboardingCompleteResult = { ok: true } | SaveFailure;

type SaveOnboardingDeps = {
  fetchFn?: typeof fetch;
  getAccessToken: () => string | null | undefined;
  refreshAccessToken?: () => Promise<string | null>;
  cloudApiUrl?: string;
  sleep?: (ms: number) => Promise<void>;
  timeoutMs?: number;
};

const RETRY_PAUSE_MS = 700;

/** POST /api/user/onboarding — retry once; surface the real failure reason, never a silent local complete (#653, #11279). */
export async function saveOnboardingComplete(
  payload: OnboardingCompletePayload,
  deps: SaveOnboardingDeps,
): Promise<SaveOnboardingCompleteResult> {
  // Late-bound plain call so whatever window.fetch is at save time runs with a Window `this` (#11472).
  const fetchImpl: typeof fetch = deps.fetchFn ?? ((input, init) => fetch(input, init));
  const baseUrl = deps.cloudApiUrl ?? CLOUD_API_URL;
  const sleep = deps.sleep ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));

  const attemptSave = (token: string) => postOnboardingAttempt({
    fetchImpl,
    url: `${baseUrl}/api/user/onboarding`,
    token,
    timeoutMs: deps.timeoutMs ?? REQUEST_TIMEOUT_MS,
    body: {
      onboarding_completed: true,
      genres: payload.genres,
      favoriteArtists: payload.artists,
      streamingService: payload.streamingService || null,
    },
  });

  let token = deps.getAccessToken()?.trim() || '';
  if (!token) return NO_SESSION_FAILURE;

  let failure: SaveFailure = NO_SESSION_FAILURE;
  for (let attempt = 0; attempt < 2; attempt++) {
    const result = await attemptSave(token);
    if (result.ok) {
      console.info(`[Onboarding] save ok attempt=${attempt + 1}`);
      return result;
    }
    failure = result;
    console.error(
      `[Onboarding] save failed attempt=${attempt + 1} reason=${failure.reason} status=${failure.status ?? 'none'}`,
      failure.detail ?? '',
    );
    if (attempt === 0) {
      const refreshed = await deps.refreshAccessToken?.().catch(() => null);
      if (refreshed?.trim()) token = refreshed.trim();
      await sleep(RETRY_PAUSE_MS);
    }
  }

  return failure;
}

export function isOnboardingWizardRoute(pathname: string): boolean {
  return pathname === '/welcome' || pathname.startsWith('/onboarding');
}
